/**
 * Privacy Policy content for SEOGeeks AI Visibility.
 *
 * Substantive starter content drawn from the Ukrainian Law on the
 * Protection of Personal Data, Law on Consumer Rights Protection,
 * and Law on Electronic Commerce. The operator should review with
 * counsel before launch — this is a sensible baseline, not legal
 * advice.
 *
 * Each language entry shares the same section keys so the page
 * renders identically across locales.
 */

const SHARED = {
  contactEmail: "ai-visibility@seogeeks.com.ua",
  lastUpdated: "2026-05-18",
};

export const PRIVACY_CONTENT = {
  uk: {
    ...SHARED,
    title: "Політика конфіденційності",
    intro:
      "Ця Політика конфіденційності описує, як сервіс SEOGeeks AI Visibility " +
      "(далі — «Сервіс») збирає, використовує, зберігає та захищає " +
      "персональні дані користувачів відповідно до Закону України «Про " +
      "захист персональних даних» та інших застосовних нормативних актів.",
    sections: [
      {
        title: "1. Які дані ми збираємо",
        body: [
          "реєстраційні дані: електронна адреса, ім'я, ідентифікатори облікового запису;",
          "технічні дані: IP-адреса, дані браузера, журнали подій та помилок, мітки часу;",
          "дані використання Сервісу: підключені сайти, налаштовані запити (промпти), результати перевірок, активність у кабінеті;",
          "платіжні дані: ідентифікатор замовлення та статус транзакції від WayForPay. Реквізити платіжних карток ми не зберігаємо.",
        ],
      },
      {
        title: "2. Як ми використовуємо дані",
        body: [
          "для надання доступу до Сервісу та його функцій;",
          "для авторизації, безпеки облікового запису та запобігання зловживанням;",
          "для покращення якості продукту та підтримки користувачів;",
          "для надсилання сервісних повідомлень (запрошення, відновлення доступу, інформація про зміни в Сервісі);",
          "для виконання вимог податкового та фінансового законодавства України.",
        ],
      },
      {
        title: "3. Правові підстави обробки",
        body:
          "Ми обробляємо персональні дані на підставі виконання договору з користувачем " +
          "(ст. 11 Закону України «Про захист персональних даних»), законного інтересу щодо " +
          "безпечної роботи Сервісу та, у визначених випадках, на підставі усвідомленої згоди " +
          "користувача (зокрема для маркетингових повідомлень).",
      },
      {
        title: "4. Передача даних третім сторонам",
        body:
          "Ми можемо залучати перевірених технологічних провайдерів — хостинг, поштові " +
          "сервіси, аналітика, платіжний шлюз WayForPay, постачальники LLM API (OpenAI, " +
          "Anthropic, Google) — які обробляють дані виключно для надання функцій Сервісу. " +
          "Ми не продаємо персональні дані третім особам та не передаємо їх для маркетингу " +
          "сторонніх компаній.",
      },
      {
        title: "5. Зберігання та видалення даних",
        body:
          "Персональні дані зберігаються протягом строку дії облікового запису та далі — " +
          "у межах, передбачених податковим та фіскальним законодавством України. Сирі " +
          "відповіді LLM-моделей видаляються автоматично через 90 днів — у кабінеті залишаються " +
          "лише агреговані метрики (рейтинг видимості, частота згадок, настрій). Користувач " +
          "може ініціювати повне видалення облікового запису з налаштувань.",
      },
      {
        title: "6. Захист даних",
        body:
          "Ми застосовуємо технічні та організаційні заходи безпеки: TLS-шифрування трафіку, " +
          "шифрування чутливих полів у базі даних (Fernet), контроль доступу за принципом " +
          "найменших привілеїв, регулярне резервне копіювання, моніторинг аномальної активності.",
      },
      {
        title: "7. Права користувача",
        body: [
          "право на доступ до своїх даних;",
          "право на виправлення неточних даних;",
          "право на видалення даних (за наявності правових підстав);",
          "право обмежити обробку;",
          "право відкликати згоду на обробку, якщо вона є правовою підставою;",
          "право подати скаргу до Уповноваженого Верховної Ради України з прав людини.",
        ],
      },
      {
        title: "8. Cookies та схожі технології",
        body:
          "Ми використовуємо cookies та локальне сховище браузера для підтримки сесії, " +
          "безпечної авторизації, збереження мови інтерфейсу та стабільної роботи Сервісу. " +
          "Аналітичні cookies третіх сторін не використовуються без окремої згоди користувача.",
      },
      {
        title: "9. Зміни до Політики",
        body:
          "Ми можемо періодично оновлювати цю Політику. Актуальна версія завжди доступна на " +
          "цій сторінці; істотні зміни ми анонсуємо через email або повідомлення в кабінеті.",
      },
      {
        title: "10. Контакти",
        body:
          "З питань обробки персональних даних звертайтесь: " + SHARED.contactEmail + ".",
      },
    ],
  },

  ru: {
    ...SHARED,
    title: "Политика конфиденциальности",
    intro:
      "Настоящая Политика конфиденциальности описывает, как сервис SEOGeeks AI " +
      "Visibility (далее — «Сервис») собирает, использует, хранит и защищает " +
      "персональные данные пользователей в соответствии с Законом Украины «О " +
      "защите персональных данных» и другими применимыми нормативными актами.",
    sections: [
      {
        title: "1. Какие данные мы собираем",
        body: [
          "регистрационные данные: электронная почта, имя, идентификаторы аккаунта;",
          "технические данные: IP-адрес, данные браузера, журналы событий и ошибок, метки времени;",
          "данные использования Сервиса: подключённые сайты, настроенные запросы (промпты), результаты проверок, активность в кабинете;",
          "платёжные данные: идентификатор заказа и статус транзакции от WayForPay. Реквизиты платёжных карт мы не храним.",
        ],
      },
      {
        title: "2. Как мы используем данные",
        body: [
          "для предоставления доступа к Сервису и его функциям;",
          "для авторизации, безопасности аккаунта и предотвращения злоупотреблений;",
          "для улучшения качества продукта и поддержки пользователей;",
          "для отправки сервисных сообщений (приглашения, восстановление доступа, информация об изменениях в Сервисе);",
          "для выполнения требований налогового и финансового законодательства Украины.",
        ],
      },
      {
        title: "3. Правовые основания обработки",
        body:
          "Мы обрабатываем персональные данные на основании исполнения договора с пользователем " +
          "(ст. 11 Закона Украины «О защите персональных данных»), законного интереса в обеспечении " +
          "безопасной работы Сервиса и, в определённых случаях, на основании осознанного согласия " +
          "пользователя (в частности, для маркетинговых сообщений).",
      },
      {
        title: "4. Передача данных третьим сторонам",
        body:
          "Мы можем привлекать проверенных технологических провайдеров — хостинг, почтовые " +
          "сервисы, аналитика, платёжный шлюз WayForPay, поставщики LLM API (OpenAI, " +
          "Anthropic, Google) — которые обрабатывают данные исключительно для предоставления " +
          "функций Сервиса. Мы не продаём персональные данные третьим лицам и не передаём их для " +
          "маркетинга сторонних компаний.",
      },
      {
        title: "5. Хранение и удаление данных",
        body:
          "Персональные данные хранятся в течение срока действия аккаунта и далее — в пределах, " +
          "предусмотренных налоговым и фискальным законодательством Украины. Сырые ответы LLM " +
          "удаляются автоматически через 90 дней — в кабинете остаются только агрегированные " +
          "метрики (рейтинг видимости, частота упоминаний, настроение). Пользователь может " +
          "инициировать полное удаление аккаунта из настроек.",
      },
      {
        title: "6. Защита данных",
        body:
          "Мы применяем технические и организационные меры безопасности: TLS-шифрование трафика, " +
          "шифрование чувствительных полей в базе данных (Fernet), контроль доступа по принципу " +
          "наименьших привилегий, регулярное резервное копирование, мониторинг аномальной активности.",
      },
      {
        title: "7. Права пользователя",
        body: [
          "право на доступ к своим данным;",
          "право на исправление неточных данных;",
          "право на удаление данных (при наличии правовых оснований);",
          "право ограничить обработку;",
          "право отозвать согласие на обработку, если оно является правовым основанием;",
          "право подать жалобу Уполномоченному Верховной Рады Украины по правам человека.",
        ],
      },
      {
        title: "8. Cookies и подобные технологии",
        body:
          "Мы используем cookies и локальное хранилище браузера для поддержания сессии, " +
          "безопасной авторизации, сохранения языка интерфейса и стабильной работы Сервиса. " +
          "Аналитические cookies третьих сторон не используются без отдельного согласия пользователя.",
      },
      {
        title: "9. Изменения Политики",
        body:
          "Мы можем периодически обновлять эту Политику. Актуальная версия всегда доступна на " +
          "этой странице; существенные изменения мы анонсируем по email или сообщением в кабинете.",
      },
      {
        title: "10. Контакты",
        body:
          "По вопросам обработки персональных данных обращайтесь: " + SHARED.contactEmail + ".",
      },
    ],
  },

  en: {
    ...SHARED,
    title: "Privacy Policy",
    intro:
      "This Privacy Policy describes how SEOGeeks AI Visibility (the " +
      "“Service”) collects, uses, stores and protects personal data of its " +
      "users in compliance with the Law of Ukraine “On Personal Data " +
      "Protection” and other applicable regulations.",
    sections: [
      {
        title: "1. Data we collect",
        body: [
          "Registration data: email address, name, account identifiers;",
          "Technical data: IP address, browser data, event and error logs, timestamps;",
          "Service usage data: connected websites, configured prompts, scan results, dashboard activity;",
          "Payment data: order ID and transaction status from WayForPay. We do not store card numbers.",
        ],
      },
      {
        title: "2. How we use the data",
        body: [
          "To provide access to the Service and its features;",
          "For authentication, account security and abuse prevention;",
          "To improve product quality and provide user support;",
          "To send service messages (invitations, password resets, product updates);",
          "To comply with Ukrainian tax and financial legislation.",
        ],
      },
      {
        title: "3. Legal basis for processing",
        body:
          "We process personal data on the basis of contractual necessity (Art. 11 of the Law " +
          "of Ukraine “On Personal Data Protection”), legitimate interest in the secure " +
          "operation of the Service, and, in defined cases, the user's informed consent " +
          "(in particular for marketing communications).",
      },
      {
        title: "4. Sharing with third parties",
        body:
          "We engage trusted technology providers — hosting, email delivery, analytics, the " +
          "WayForPay payment gateway, and LLM API providers (OpenAI, Anthropic, Google) — " +
          "which process data solely to deliver Service features. We do not sell personal data " +
          "to third parties and do not transfer it for third-party marketing.",
      },
      {
        title: "5. Retention and deletion",
        body:
          "Personal data is retained for the term of the account and thereafter only as " +
          "required by Ukrainian tax and fiscal law. Raw LLM responses are deleted automatically " +
          "after 90 days — only aggregated metrics (visibility score, mention rate, sentiment) " +
          "remain in the dashboard. Users can permanently delete their account from settings.",
      },
      {
        title: "6. Data security",
        body:
          "We apply technical and organisational safeguards: TLS encryption in transit, " +
          "Fernet encryption of sensitive database fields, least-privilege access control, " +
          "regular backups, and monitoring of anomalous activity.",
      },
      {
        title: "7. Your rights",
        body: [
          "Access to your data;",
          "Correction of inaccurate data;",
          "Deletion of data (where legally permitted);",
          "Restriction of processing;",
          "Withdrawal of consent where consent is the legal basis;",
          "The right to lodge a complaint with the Ukrainian Parliament Commissioner for Human Rights.",
        ],
      },
      {
        title: "8. Cookies and similar technologies",
        body:
          "We use cookies and browser local storage to maintain sessions, support secure " +
          "authentication, persist interface language, and ensure stable operation. We do " +
          "not use third-party analytics cookies without separate user consent.",
      },
      {
        title: "9. Changes to the policy",
        body:
          "We may update this policy from time to time. The current version is always available " +
          "on this page; material changes are announced by email or in-app notice.",
      },
      {
        title: "10. Contact",
        body:
          "For questions about personal data, contact: " + SHARED.contactEmail + ".",
      },
    ],
  },
};
